// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import {
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  MoreOutlined,
  SendOutlined,
  StopOutlined,
  UpOutlined
} from "@ant-design/icons/lib";
import { navigate } from "@reach/router";
import { Button, Popover } from "antd";
import i18n from "i18next";
import React from "react";
import { ThematicDossierInfoResponseDTO } from "../../../../api/backend-openapi";
import {
  requestDecreaseOrderThematicDossier,
  requestDeleteThematicDossier,
  requestIncreaseOrderThematicDossier,
  requestWorkflowThematicDossier
} from "../../../../state/thematicDossier/ThematicDossierEffects";
import {
  THEMATIC_DOSSIER_STATUS,
  alignRight
} from "../../../../utils/Constant";
import DateFormat from "../../../../utils/DateFormat";
import { showConfirm } from "../../../components/ConfirmModal";
import { tooltipRender } from "../../../components/CustomTooltip";
import TableButton from "../../../components/TableButton";
import ThematicDossierStatusTag from "../details/ThematicDossierStatusTag";

const popoverContent = (
  record: ThematicDossierInfoResponseDTO,
  canValidate: boolean,
  canDelete: boolean,
  thematicDossierList: ThematicDossierInfoResponseDTO[] | null
) => (
  <div className="d-inline-flex">
    <TableButton
      icon={<EyeOutlined />}
      tooltipTitle={i18n.t("thematic_dossiers.table.tooltip.view")}
      onClick={() => {
        navigate(`/thematicDossiers/${record.id}`);
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    {record.publicationDate === null && canValidate && (
      <TableButton
        icon={<SendOutlined />}
        buttonClassName="btn-success mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("thematic_dossiers.table.confirmation.publish", {
              rubrique: record.name
            }),
            () => {
              requestWorkflowThematicDossier({
                id: record.id,
                dto: {
                  published: true
                }
              });
            }
          )
        }
        tooltipTitle={i18n.t("thematic_dossiers.table.tooltip.publish")}
      />
    )}
    {record.publicationDate && canValidate && (
      <TableButton
        icon={<StopOutlined />}
        buttonClassName="btn-secondary mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("thematic_dossiers.table.confirmation.unpublish", {
              rubrique: record.name
            }),
            () => {
              requestWorkflowThematicDossier({
                id: record.id,
                dto: {
                  published: false
                }
              });
            }
          )
        }
        tooltipTitle={i18n.t("thematic_dossiers.table.tooltip.unpublish")}
      />
    )}
    {record.publicationDate === null && canDelete && (
      <TableButton
        icon={<DeleteOutlined />}
        buttonClassName="btn-danger mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("thematic_dossiers.table.confirmation.delete", {
              rubrique: record.name
            }),
            () => {
              requestDeleteThematicDossier(record);
            }
          )
        }
        tooltipTitle={i18n.t("thematic_dossiers.table.tooltip.delete")}
      />
    )}
    {canValidate && thematicDossierList && record.order > 0 && (
      <TableButton
        icon={<UpOutlined />}
        buttonClassName="btn-order mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("thematic_dossiers.table.confirmation.upThematicDossier", {
              rubrique: record.name
            }),
            () => {
              requestIncreaseOrderThematicDossier(record);
            }
          )
        }
        tooltipTitle={i18n.t(
          "thematic_dossiers.table.tooltip.upThematicDossier"
        )}
      />
    )}
    {canValidate &&
      !!thematicDossierList &&
      record.order <
        thematicDossierList?.reduce(
          (acc, value) => (value.order > acc ? value.order : acc),
          0
        ) && (
        <TableButton
          icon={<DownOutlined />}
          buttonClassName="btn-order mr-1 btn-list"
          onClick={() =>
            showConfirm(
              i18n.t(
                "thematic_dossiers.table.confirmation.downThematicDossier",
                {
                  rubrique: record.name
                }
              ),
              () => {
                requestDecreaseOrderThematicDossier(record);
              }
            )
          }
          tooltipTitle={i18n.t(
            "thematic_dossiers.table.tooltip.downThematicDossier"
          )}
        />
      )}
  </div>
);

export const columns = (
  getColumnSearchProps: any,
  canValidate: boolean,
  canDelete: boolean,
  thematicDossierList: ThematicDossierInfoResponseDTO[] | null
) => {
  return [
    {
      title: i18n.t("thematic_dossiers.table.name"),
      key: "name",
      ellipsis: true,
      render: tooltipRender,
      ...getColumnSearchProps()
    },
    {
      title: i18n.t("thematic_dossiers.table.order"),
      key: "order",
      dataIndex: "order",
      render: (order: number) =>
        order !== null && order !== undefined ? order + 1 : "",
      ellipsis: true,
      width: "5%"
    },
    {
      title: i18n.t("thematic_dossiers.table.status"),
      key: "status",
      ellipsis: true,
      filters: [
        { text: THEMATIC_DOSSIER_STATUS.PUBLISHED.label, value: "published" },
        {
          text: THEMATIC_DOSSIER_STATUS.UNPUBLISHED.label,
          value: "unpublished"
        }
      ],
      defaultFilteredValue: undefined,
      onFilter: (value: string, record: ThematicDossierInfoResponseDTO) => {
        if (value === "published") {
          return record.publicationDate;
        } else if (value === "unpublished") {
          return !record.publicationDate;
        } else {
          return true;
        }
      },
      render: (record: ThematicDossierInfoResponseDTO) => {
        return (
          record && (
            <ThematicDossierStatusTag
              publicationDate={record.publicationDate}
            />
          )
        );
      }
    },
    {
      title: i18n.t("thematic_dossiers.table.publication_date"),
      key: "publicationDate",
      ellipsis: true,
      render: (record: ThematicDossierInfoResponseDTO) => {
        return (
          <span>
            {record.publicationDate
              ? DateFormat.standardDate(record.publicationDate)
              : ""}
          </span>
        );
      }
    },
    {
      title: i18n.t("thematic_dossiers.table.creation_date"),
      key: "creationDate",
      dataIndex: "creationDate",
      ellipsis: true,
      render: (record: Date) => {
        return <span>{DateFormat.standardDate(record)}</span>;
      }
    },
    {
      title: i18n.t("thematic_dossiers.table.modification_date"),
      key: "modificationDate",
      dataIndex: "modificationDate",
      ellipsis: true,
      render: (record: Date) => {
        return <span>{DateFormat.standardDate(record)}</span>;
      }
    },
    {
      title: i18n.t("thematic_dossiers.table.action"),
      key: "action",
      fixed: alignRight,
      width: 90,
      onCell: () => {
        return {
          onClick: (e: any) => e.stopPropagation()
        };
      },
      render: (text: string, record: ThematicDossierInfoResponseDTO) => (
        <Popover
          content={popoverContent(
            record,
            canValidate,
            canDelete,
            thematicDossierList
          )}
          placement="bottomRight"
          trigger="hover"
        >
          <Button
            icon={<MoreOutlined />}
            type="text"
            size="large"
            className="btn-list"
          />
        </Popover>
      )
    }
  ];
};
